import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Bildirim şeridi (toast).
 *
 * Eski panelde her işlem `alert('... başarıyla güncellendi')` diyordu — sunucu
 * hata verse bile. Burada iki ayrı tip var ve HATA MESAJI SUNUCUDAN GELDİĞİ
 * GİBİ gösterilir. Hata toast'ı kendiliğinden kapanmaz; kullanıcı okumadan
 * kaybolmasın diye kapatma düğmesi zorunludur.
 */

export type ToastKind = "success" | "error" | "info";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  /** Uzun sunucu detayları (ör. hangi kayıt atlandı). */
  detail?: string;
}

interface ToastApi {
  success: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastApi>({
  success: () => {},
  error: () => {},
  info: () => {},
  dismiss: () => {},
});

const STYLE: Record<ToastKind, string> = {
  success: "border-green-300 bg-green-50 text-green-900",
  error: "border-red-300 bg-red-50 text-red-900",
  info: "border-primary-300 bg-primary-50 text-primary-900",
};

const TITLE: Record<ToastKind, string> = {
  success: "Tamamlandı",
  error: "Hata",
  info: "Bilgi",
};

/**
 * Tek başına bildirim kutusu — sağlayıcıya bağlı değil.
 *
 * Çoğu ekran `useToast()` kullanır (kuyruk, otomatik kapanma, yığma). Bu bileşen
 * bildirimin belirli bir yerde, sayfa akışının içinde durması gerektiği
 * durumlar içindir (ör. formun hemen üstünde kalması istenen hata).
 */
export function Toast({
  kind,
  message,
  detail,
  onClose,
}: {
  kind: ToastKind;
  message: string;
  detail?: string;
  onClose?: () => void;
}) {
  return (
    <div
      role={kind === "error" ? "alert" : "status"}
      className={`mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${STYLE[kind]}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">{TITLE[kind]}</p>
        <p className="mt-0.5 break-words">{message}</p>
        {detail ? <p className="mt-1 break-words opacity-80">{detail}</p> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Bildirimi kapat"
          className="shrink-0 inline-flex items-center justify-center rounded min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0 lg:p-1 opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, detail?: string) => {
      const id = ++seq.current;
      setItems((prev) => [...prev.slice(-3), { id, kind, message, detail }]);
      // Başarı/bilgi kendiliğinden kapanır; HATA KAPANMAZ.
      if (kind !== "error") {
        window.setTimeout(() => dismiss(id), 4500);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, d) => push("success", m, d),
      error: (m, d) => push("error", m, d),
      info: (m, d) => push("info", m, d),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        className="noprint pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.kind === "error" ? "alert" : "status"}
            className={`pointer-events-auto w-full max-w-md rounded-lg border px-4 py-3 shadow-lg ${STYLE[t.kind]}`}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
                  {TITLE[t.kind]}
                </p>
                <p className="mt-0.5 whitespace-pre-line break-words text-sm font-medium">
                  {t.message}
                </p>
                {t.detail ? (
                  <p className="mt-1 whitespace-pre-line break-words text-xs opacity-80">
                    {t.detail}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="-m-1 shrink-0 inline-flex items-center justify-center rounded min-h-[44px] min-w-[44px] text-current opacity-60 hover:opacity-100 lg:min-h-0 lg:min-w-0 lg:p-1"
                aria-label="Bildirimi kapat"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(Ctx);
}

export default ToastProvider;
